// eslint-disable-next-line import/no-unresolved
import {UAParserService} from 'Services/UAParserService';

const isSafari = () => UAParserService.getBrowser().name === 'Safari';
const ARIA = {states: {selected: 'aria-selected'}};

/**
 * @description Script allow to switch tab and display the associate content.
 * @param {HTMLElement} domElement - The container that contain the trigger trigger for the TabManager.
 */
export default class TabManager {
    // eslint-disable-next-line complexity, max-statements
    constructor(domElement) {
        if(!domElement) {
            throw new Error('element doesn\'t exist');
        }

        if(!domElement.hasAttribute('data-tabmanager-id')) {
            throw new Error('data-tabmanager-id is missing');
        }

        const tabManagerId = domElement.dataset.tabmanagerId;

        if(!tabManagerId) {
            throw new Error('no ID set on the data-tabmanager-id attribute');
        }

        const domTabControllers = domElement.querySelector(`[data-tab-controllers="${tabManagerId}"]`);
        const domTabContent = domElement.querySelector(`[data-tab-content="${tabManagerId}"]`);

        if(!domTabControllers || !domTabContent) {
            throw new Error('The data-tab-controllers and/or data-tab-content value doesn\'t match the tabmanager-id or value are missing');
        }

        const tabActiveClass = domElement.getAttribute('data-tab-active');
        const contentActiveClass = domElement.getAttribute('data-content-active');

        if(!tabActiveClass || !contentActiveClass) {
            throw new Error('The data-tab-active and/or data-content-active are missing or value are missing');
        }

        if(domElement.getAttribute('data-tab-manager-initialized')) {
            throw new Error('TabManager already initialized');
        }

        domElement.dataset.tabManagerInitialized = 'true';

        this.domElement = domElement;
        this.isActiveTabUntrigger = (this.domElement.getAttribute('data-tab-toggler') || 'false') === 'true';
        this.setActiveBind = this.setActive.bind(this);
        this.setInactiveBind = this.setInactive.bind(this);
        this.config = {
            contentActiveClass,
            domTabContent,
            domTabControllers,
            tabActiveClass,
            tabManagerId
        };

        this.initListeners();
    }

    /**
     * @description Remove a class to the active and add it to nextActive.
     * @param {HTMLElement} domActive - Current element with className.
     * @param {HTMLElement} domNextActive - Next target element to add class.
     * @param {string} className - Class To add.
     */
    // eslint-disable-next-line class-methods-use-this
    activeClassSwitcher(domActive, domNextActive, className) {
        if(domActive) {
            domActive.classList.toggle(className);
        }

        domNextActive.classList.toggle(className);
    }

    /**
     * @description Set aria selected false to the active and sets true to nextActive.
     * @param {HTMLElement} domActive - Current active element.
     * @param {HTMLElement} domNextActive - Next target element to add class.
     */
    // eslint-disable-next-line class-methods-use-this
    ariaSelectedSwitcher(domActive, domNextActive) {
        if(domActive) {
            domActive.setAttribute(ARIA.states.selected, 'false');
        }
        domNextActive.setAttribute(ARIA.states.selected, 'true');
    }

    // eslint-disable-next-line class-methods-use-this
    titleAttributeSwitcher(domActive, domNextActive) {
        if(domActive) {
            domActive.removeAttribute('title');
        }
        domNextActive.setAttribute('title', 'Rubrique active');
    }

    /**
     * @description Add the active class to the container that has the same data-tab id.
     * @param {string} id - The tab number to select the content that have the same id.
     */
    setContentActive(id) {
        const domCurrentActive = this.config.domTabContent.querySelector(`.${this.config.contentActiveClass}`);
        const domContentTarget = this.config.domTabContent.querySelector(`[data-tab=${id}]`);

        if(domCurrentActive === domContentTarget) {
            return;
        }

        this.activeClassSwitcher(domCurrentActive, domContentTarget, this.config.contentActiveClass);
    }

    /**
     * @description Add the active class to the tab.
     * @param {HTMLElement} domTarget - HTMLElement that have been click.
     */
    setTabActive(domTarget) {
        const domCurrentActiveTab = this.config.domTabControllers.querySelector(`.${this.config.tabActiveClass}`);

        if(domTarget === domCurrentActiveTab) {
            return;
        }

        this.activeClassSwitcher(domCurrentActiveTab, domTarget, this.config.tabActiveClass);
        this.ariaSelectedSwitcher(domCurrentActiveTab, domTarget);
        this.titleAttributeSwitcher(domCurrentActiveTab, domTarget);
    }

    /**
     * @description Set active the tab and the associated content.
     * @param {Event} event - Add the active class to the target on click.
     */
    setActive(event) {
        const tabId = event.currentTarget.dataset.tab;

        this.setTabActive(event.currentTarget);
        this.setContentActive(tabId);

        if(this.isActiveTabUntrigger) {
            this.TabMenuToggler('add');
            this.automaticEventDispatcher();
        }
    }

    /**
     * @description Set active the tab and the associated content.
     */
    automaticEventDispatcher() {
        const domTabsList = [...this.config.domTabControllers.querySelectorAll('[data-tab]')];

        domTabsList.forEach(domItem => {
            domItem.removeEventListener('click', this.setActiveBind, false);
            domItem.removeEventListener('click', this.setInactiveBind, false);
            domItem.addEventListener('click', domItem.classList.contains(this.config.tabActiveClass) ? this.setInactiveBind : this.setActiveBind);
        });
    }

    /**
     * @description Activate the component on first click.
     * @param {string} [action=remove] - Action to perform.
     */
    TabMenuToggler(action = 'remove') {
        const domCloseContentList = [...this.domElement.querySelectorAll('[data-tabcontainer-toggler]')];

        domCloseContentList.forEach(domItem => {
            domItem.classList[action](domItem.dataset.tabcontainerToggler);
        });
    }

    /**
     * @description Set the tab inactive.
     */
    setTabInactive() {
        const domActiveController = this.config.domTabControllers.querySelector(`.${this.config.tabActiveClass}`);

        if(domActiveController) {
            domActiveController.blur();
            domActiveController.classList.remove(this.config.tabActiveClass);
            domActiveController.setAttribute(ARIA.states.selected, 'false');
        }
    }

    /**
     * @description Set the content inactive.
     */
    setContentInactive() {
        const activeContent = this.config.domTabContent.querySelector(`.${this.config.contentActiveClass}`);

        if(activeContent) {
            activeContent.classList.remove(this.config.contentActiveClass);
        }
    }

    /**
     * @description Remove all the active class.
     */
    setInactive() {
        this.setTabInactive();
        this.setContentInactive();

        if(this.isActiveTabUntrigger) {
            this.TabMenuToggler();
            this.automaticEventDispatcher();
        }
    }

    /**
     * @description Add listener to all necessary element.
     */
    initListeners() {
        const domTabsList = [...this.config.domTabControllers.querySelectorAll('[data-tab]')];
        const domCloseContentsList = [...this.domElement.querySelectorAll(`[data-tab-inactive="${this.config.tabManagerId}"]`)];

        domTabsList.forEach(domItem => {
            domItem.addEventListener('click', this.setActiveBind);
        });

        if(domCloseContentsList) {
            domCloseContentsList.forEach(domItem => {
                domItem.addEventListener('click', this.setInactiveBind);
            });
        }

        if(!isSafari) {
            // eslint-disable-next-line no-undef
            window.addEventListener('resize', this.setInactiveBind);
        }
    }
}
